import React, { useState, FormEvent, ChangeEvent, useRef } from 'react';
import {
    Typography,
    Paper,
    Grid,
    Divider,
    CircularProgress,
    TextField,
    Button,
    IconButton,
    TextareaAutosize,
} from '@mui/material';
import {
    HiOutlinePhotograph,
    HiOutlineUpload,
    HiOutlineTrash
} from 'react-icons/hi';


interface SitePhoto {
    id: number;
    url: string;
}

interface SiteSettings {
    site_title?: string;
    site_description?: string;
    email?: string;
    about_us?: string;
    telegram_link?: string;
    instagram_link?: string;
    youtube_link?: string;
    castbox_link?: string;
    site_photos?: SitePhoto[];
}

interface SiteSettingsTabProps {
    apiFetch: (url: string, options?: RequestInit) => Promise<any>;
    handleApiCall: (apiCall: Promise<any>, successMessage: string) => Promise<any>;
    siteSettings: Partial<SiteSettings>;
    setSiteSettings: React.Dispatch<React.SetStateAction<Partial<SiteSettings>>>;
    loading: boolean;
    setError: (error: string | null) => void;
}

const markdownStyles = `
.markdown-display-wrapper {
    background-color: rgba(0, 0, 0, 0.05);
    border: 1px solid var(--border-color, #444);
    border-radius: 8px;
    padding: 1rem 1.5rem;
    margin-top: 0.5rem;
    color: var(--text-color);
}
.markdown-display-wrapper h1,
.markdown-display-wrapper h2,
.markdown-display-wrapper h3 {
    border-bottom: 1px solid var(--border-color, #444);
    padding-bottom: 0.4em;
    margin-top: 1.2em;
    margin-bottom: 0.8em;
    color: var(--primary-color, #29b6f6);
}
.markdown-display-wrapper p {
    line-height: 1.8;
    margin-bottom: 1em;
    font-size: 0.95rem;
}
.markdown-display-wrapper ul,
.markdown-display-wrapper ol {
    padding-right: 25px;
    margin-bottom: 1em;
}
.markdown-display-wrapper li {
    margin-bottom: 0.6em;
}
.markdown-display-wrapper a {
    color: var(--primary-color, #29b6f6);
    text-decoration: none;
}
.markdown-display-wrapper a:hover {
    text-decoration: underline;
}
.markdown-display-wrapper strong {
    color: var(--text-color);
    font-weight: 600;
}
.markdown-display-wrapper code {
    background-color: rgba(128, 128, 128, 0.2);
    padding: 2px 5px;
    border-radius: 4px;
    font-family: 'monospace';
}
`;

const SiteSettingsTab: React.FC<SiteSettingsTabProps> = ({
    apiFetch,
    handleApiCall,
    siteSettings,
    setSiteSettings,
    loading,
    setError
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [photoUploadLoading, setPhotoUploadLoading] = useState(false);

    const handleSettingsChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setSiteSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setPhotoUploadLoading(true);
        setError(null);

        const uploadPromises = Array.from(files).map(async file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('tag', 'site');
            try {
                const response = await apiFetch('/admin/upload', { method: 'POST', body: formData });
                
                const data = response.data || response;

                if (data && data.id && data.path) {
                    
                    const finalUrl = data.path.startsWith('http') 
                        ? data.path 
                        : `https://api.neontradepro.com/${data.path}`; 

                    return {
                        id: data.id,
                        url: finalUrl 
                    };
                }
                
                console.error("ساختار پاسخ ناهمخوان:", response);
                return null;

            } catch (uploadError: any) {
                setError(uploadError.message);
                return null;
            }
        });

        const results = await Promise.all(uploadPromises);
        const newPhotos = results.filter((photo): photo is SitePhoto => photo !== null);
        
        setSiteSettings(prev => ({ ...prev, site_photos: [...(prev.site_photos || []), ...newPhotos] }));
        setPhotoUploadLoading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = (idToRemove: number) => {
        setSiteSettings(prev => ({ ...prev, site_photos: (prev.site_photos || []).filter(photo => photo.id !== idToRemove) }));
    };

    const handleSiteSettingsSave = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!siteSettings || Object.keys(siteSettings).length === 0) {
            setError('اطلاعاتی برای ذخیره وجود ندارد.');
            return;
        }
        setSaveLoading(true);
        const photoIds = siteSettings.site_photos?.map(photo => photo.id) || [];
        const payload = { ...siteSettings, site_photos: photoIds };
        try {
            await handleApiCall(apiFetch('/settings/site', { method: 'PATCH', body: JSON.stringify(payload) }), 'تنظیمات سایت با موفقیت به‌روز شد.');
        } finally {
            setSaveLoading(false);
        }
    };

    return (
        <>
            <style>{markdownStyles}</style>
            <Paper component='form' className='management-card site-settings-form' onSubmit={handleSiteSettingsSave}>
                <h3 className='tab-section-header'>مدیریت محتوای سایت</h3>
                {loading ? <CircularProgress /> : (
                    <Grid container spacing={4}> 
                        <Grid item xs={12}>
                            <TextField name='site_title' label='عنوان اصلی سایت (Hero Title)' value={siteSettings.site_title || ''} onChange={handleSettingsChange} fullWidth variant='outlined' />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>توضیحات اصلی سایت (Hero Description)</Typography>
                            <TextareaAutosize name='site_description' value={siteSettings.site_description || ''} onChange={handleSettingsChange} minRows={3} placeholder='توضیحات اصلی سایت را با فرمت مارک‌داون بنویسید...' className='custom-textarea' />
                           
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>متن کامل صفحه درباره ما</Typography>
                            <TextareaAutosize name='about_us' value={siteSettings.about_us || ''} onChange={handleSettingsChange} minRows={10} placeholder='متن صفحه درباره ما را با فرمت مارک‌داون بنویسید...' className='custom-textarea' />
                          
                        </Grid>

                        <Grid item xs={12}><Divider sx={{ my: 2 }} /><Typography variant='h6' className='form-sub-header' sx={{display: 'flex', alignItems: 'center', gap: 1}}><HiOutlinePhotograph /> تصاویر اسلایدر صفحه اصلی</Typography></Grid>
                        <Grid item xs={12}>
                            <input accept="image/*" style={{ display: 'none' }} id="raised-button-file" multiple type="file" onChange={handleImageUpload} ref={fileInputRef} />
                            <label htmlFor="raised-button-file">
                                <Button variant="outlined" component="span" startIcon={<HiOutlineUpload />} disabled={photoUploadLoading}>
                                    {photoUploadLoading ? <CircularProgress size={20} /> : 'آپلود تصاویر جدید'}
                                </Button>
                            </label>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>برای ذخیره نهایی لیست عکس‌ها، باید روی دکمه "ذخیره تغییرات" در انتهای فرم کلیک کنید.</Typography>
                        </Grid>
                        {siteSettings.site_photos && siteSettings.site_photos.length > 0 && (
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    {siteSettings.site_photos.filter(photo => photo && photo.url).map((photo) => (
                                        <Grid item key={photo.id} xs={6} sm={4} md={3}>
                                            <Paper sx={{ position: 'relative', overflow: 'hidden', borderRadius: '8px' }}>
                                                <img src={photo.url} alt={`site photo ${photo.id}`} style={{ width: '100%', height: 'auto', display: 'block' }} />
                                                <IconButton size="small" onClick={() => handleRemoveImage(photo.id)}
                                                    sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255, 255, 255, 0.8)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' } }}>
                                                    <HiOutlineTrash color='red'/>
                                                </IconButton>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        )}

                        <Grid item xs={12}><Divider sx={{ my: 2 }} /><Typography variant='h6' className='form-sub-header'>لینک‌های ارتباطی</Typography></Grid>
                        <Grid item xs={12} md={6}><TextField name='email' label='ایمیل' value={siteSettings.email || ''} onChange={handleSettingsChange} fullWidth variant='outlined' /></Grid>
                        <Grid item xs={12} md={6}><TextField name='telegram_link' label='لینک تلگرام' value={siteSettings.telegram_link || ''} onChange={handleSettingsChange} fullWidth variant='outlined' /></Grid>
                        <Grid item xs={12} md={6}><TextField name='instagram_link' label='لینک اینستاگرام' value={siteSettings.instagram_link || ''} onChange={handleSettingsChange} fullWidth variant='outlined' /></Grid>
                        <Grid item xs={12} md={6}><TextField name='youtube_link' label='لینک یوتیوب' value={siteSettings.youtube_link || ''} onChange={handleSettingsChange} fullWidth variant='outlined' /></Grid>
                        <Grid item xs={12} md={6}><TextField name='castbox_link' label='لینک کست‌باکس' value={siteSettings.castbox_link || ''} onChange={handleSettingsChange} fullWidth variant='outlined' /></Grid>

                        <Grid item xs={12}>
                            <Button type='submit' variant='contained' color='primary' disabled={saveLoading} sx={{ mt: 2, px: 4, py: 1 }}>
                                {saveLoading ? <CircularProgress size={24} /> : 'ذخیره کل تغییرات'}
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Paper>
        </>
    );
};

export default SiteSettingsTab;